import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  setGlobalSearchReset,
  restoreStateSnapshot,
  setHospitalsDetails,
} from "../../Redux/app/app.actions";
import {
  resetSelectedState,
  resetSpecificStateData,
  setSelectedHospital,
} from "../../Redux/hospital/hospital.actions";
import { useFilters } from "../../hooks/useFilters";
import {
  Input,
  ListElement,
  SearchBoxContainer,
  SuggestionsList,
  SuggestionsScroll,
} from "./index.styled";
import { ReactComponent as IconSearch } from "../../Assets/Icons/IconSearch.svg";
import { ReactComponent as CrossIcon } from "../../Assets/Icons/CrossSolid.svg";
import debounce from "lodash.debounce";
import { useQueryClient } from "@tanstack/react-query";
import { getHospitalsByUrl } from "../../services/apiService";
import { Loader } from "../../App";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BaseContainer } from "../Header/index.styled";
import { StateAbbrv, views } from "../../Configs/config";
import {
  formatHospitalDetails,
  viewSizeCalculator,
} from "../../utils";
import { buildHospitalsUrl } from "../../utils/buildHospitalsUrl";
import { NotFoundWrapper, Text } from "../OptionsList/index.styled";

const SearchBox = ({
  placeholder = "Search Hospitals, State, County, Zip...",
}) => {
  const { isGlobalSearchReset } = useSelector((store) => store.app);
  const {
    partnerExclusiveFlag,
    globalFilter,
    quickFilter,
    search: urlSearch,
    sortBy,
    sortOrder,
  } = useFilters();
  const { selectedHospital } = useSelector((store) => store.hospital);
  const reduxStore = useStore();
  // Tracks the previous global-search-reset flag so we can detect an external
  // reset (e.g. the view-switch "reset search?" popup) and clear the input.
  const prevGlobalSearchResetRef = useRef(isGlobalSearchReset);
  // Tracks the previous URL search term (`q`) so we can detect when an active
  // search is cleared by another action (e.g. applying advanced filters, which
  // can't be combined with a text search) and clear the input to match.
  const prevUrlSearchRef = useRef(urlSearch);
  // Holds an exact snapshot of the data context (filters, view, selected
  // state, results, URL, etc.) captured the moment a search is applied, so
  // clearing the search can restore the pre-search state faithfully.
  const preSearchSnapshotRef = useRef(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchTableData, setSearchTableData] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponseReceived, setApiResponseReceived] = useState(false);
  const [clickedEnter, setClickedEnter] = useState(false);
  const [stateAbbrv, setStateAbbrv] = useState(""); // Need to remove when we will use NET Income revenue for all states for sorting
  // No use for above because we are using provider number for couting stats
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Applies a search to the URL atomically: clears advanced filters + map/legend
  // selection, then writes the search term (`q`) and, for a state search, the
  // resolved state. Keeps base filters
  // (partnerExclusiveArea/globalFilter/quickFilter/sort). The URL is the single
  // source of truth for search too, so LeftSection fetches/paginates/exports
  // from `q` and a refresh/shared link restores the results.
  const applySearch = (term, abbrv) => {
    const keep = new Set([
      "partnerExclusiveArea",
      "globalFilter",
      "quickFilter",
      "sort_by",
      "sort_order",
    ]);
    const next = new URLSearchParams(searchParams);
    [...next.keys()].forEach((key) => {
      if (!keep.has(key)) next.delete(key);
    });
    if (term) next.set("q", term);
    if (abbrv) next.set("state", abbrv);
    // A search always lands on the table. Setting the view here (rather than a
    // separate dispatch/write) keeps it a single URL update with no race.
    next.set("view", views.TABLE);
    setSearchParams(next);
    // A search is not an advanced-filter state. Clearing filterFlag here (a
    // search replaces any active advanced filters) keeps LeftSection's fetch
    // routing unambiguous: with filterFlag false it takes the text-search branch
    // rather than mistaking the transient (filterFlag still true from a prior
    // filter, `q` in the URL) for a mid-apply race and deferring the fetch.
    dispatch(setHospitalsDetails({ filterFlag: false }));
  };

  const onSearch = () => {
    if (selectedHospital?.provider_number && suggestions.length > 0) {
      dispatch(setGlobalSearchReset(true));
      if (query) navigate(`/hospitals/${selectedHospital?.provider_number}`);
    }
  };

  // Capture the exact data context right before a search is applied.
  // Only the first capture is kept, so chaining searches without clearing
  // still restores the original pre-search state.
  const capturePreSearchSnapshot = () => {
    if (preSearchSnapshotRef.current) return;
    const { app, hospital } = reduxStore.getState();
    preSearchSnapshotRef.current = {
      // Filter state (globalFilter/quickFilter/states/advanced filters) now
      // lives in the URL and is restored via `searchParamsString`, so only the
      // non-filter app state is snapshotted here.
      app: {
        tableViewData: app.tableViewData,
        view: app.view,
        countByProvidernumber: app.countByProvidernumber,
        isBookmarkTable: app.isBookmarkTable,
        isGlobalSearchReset: app.isGlobalSearchReset,
      },
      hospital: {
        selectedStateData: hospital.selectedStateData,
        specificStateData: hospital.specificStateData,
      },
      searchParamsString: searchParams.toString(),
    };
  };

  // Clear the search input and restore the exact pre-search state
  // (filters, view, selected state, results, URL) without a view switch.
  const handleClearSearch = () => {
    const snapshot = preSearchSnapshotRef.current;

    setQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    setClickedEnter(false);
    setApiResponseReceived(false);
    setSearchTableData([]);
    setStateAbbrv("");

    if (!snapshot) return;

    // Restore app + hospital slice state atomically
    dispatch(
      restoreStateSnapshot({
        app: snapshot.app,
        hospital: snapshot.hospital,
      }),
    );

    // Restore URL query params (filters, states, q, legendFilter, etc.).
    // Because filters now live in the URL, this also restores the pre-search
    // filter context and triggers the panels to re-fetch automatically.
    setSearchParams(new URLSearchParams(snapshot.searchParamsString));

    preSearchSnapshotRef.current = null;
  };

  const findStateAsAbbrevistion = (raw) => {
    const query = raw?.trim();
    if (!query) return undefined;

    const lower = query.toLowerCase();
    const upper = query.toUpperCase();
    const stateNames = Object.keys(StateAbbrv);

    // Exact 2-letter abbreviation (e.g. "PA", "pa")
    if (query.length === 2) {
      const name = stateNames.find((n) => StateAbbrv[n] === upper);
      if (name) return { state: name, abbreviation: upper };
    }

    // Exact full state name (e.g. "Pennsylvania")
    const exactName = stateNames.find((n) => n.toLowerCase() === lower);
    if (exactName) {
      return { state: exactName, abbreviation: StateAbbrv[exactName] };
    }

    // Unique prefix match for longer queries only (e.g. "Penn" → Pennsylvania).
    // Single/short letters like "P" must not auto-select a state.
    if (query.length >= 3) {
      const prefixMatches = stateNames.filter((n) =>
        n.toLowerCase().startsWith(lower),
      );
      if (prefixMatches.length === 1) {
        return {
          state: prefixMatches[0],
          abbreviation: StateAbbrv[prefixMatches[0]],
        };
      }
    }

    return undefined;
  };

  const fetchSuggestions = async (searchTerm) => {
    setIsLoading(true);
    setApiResponseReceived(false);
    const stateInfo = findStateAsAbbrevistion(searchTerm);
    // Apply the active global/quick/partner filters so search results respect
    // them too (search + global filter combine). The URL is built via the shared
    // builder so the suggestions request matches what LeftSection later derives
    // from the `q` param.
    let searchURL = "";
    if (stateInfo) {
      searchTerm = stateInfo.abbreviation;
      // A typed state name is a state-scoped search: routing to the state filter
      // endpoint (states present) mirrors the applied `q` + `state` URL.
      // Include page/per_page + sort so this URL matches LeftSection's first
      // fetch and shares the ["hospitals", url] React Query cache entry.
      searchURL = buildHospitalsUrl({
        search: searchTerm,
        states: [stateInfo.abbreviation],
        partnerExclusiveFlag,
        globalFilter,
        quickFilter,
        sortBy: sortBy || "ranking",
        sortOrder: sortOrder || "asc",
        page: 1,
        perPage: 50,
      });
      setStateAbbrv(stateInfo.abbreviation);
    } else {
      setStateAbbrv("");
      searchURL = buildHospitalsUrl({
        search: searchTerm,
        partnerExclusiveFlag,
        globalFilter,
        quickFilter,
        sortBy: sortBy || "ranking",
        sortOrder: sortOrder || "asc",
        page: 1,
        perPage: 50,
      });
    }

    try {
      // Same query key as LeftSection.fetchHospitalData so Enter / table load
      // reuses this response instead of issuing a second network call.
      const jsonData = await queryClient.fetchQuery({
        queryKey: ["hospitals", searchURL],
        queryFn: ({ signal }) => getHospitalsByUrl(searchURL, signal),
        staleTime: 5 * 60 * 1000,
      });
      setIsLoading(false);
      setApiResponseReceived(true);

      const filteredData = jsonData?.data?.filter(
        (item) => item.hosp10_name !== null,
      );

      const hospitalDetailsArray = formatHospitalDetails(filteredData);
      setSearchTableData(hospitalDetailsArray);
      return jsonData.data || [];
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      return [];
    }
  };

  const handleSearchTableView = () => {
    if (searchTableData.length !== 0 && apiResponseReceived) {
      if (location.pathname !== "/") {
        navigate("/");
      }
      // Search is URL-driven: applySearch() writes `q` (+ resolved state) and
      // view=table in a single navigation (which also exits compare if open).
      // LeftSection then fetches/paginates/exports the results from the URL.
      applySearch(query, stateAbbrv);
      dispatch(setGlobalSearchReset(true));
      setShowSuggestions(false);
      setApiResponseReceived(false);
      setClickedEnter(false);
      setStateAbbrv("");
    }
  };

  useEffect(() => {
    if (apiResponseReceived && searchTableData.length !== 0 && clickedEnter) {
      if (location.pathname !== "/") {
        navigate("/");
      }
      // applySearch() writes `q` (+ resolved state) and view=table in a single
      // navigation; LeftSection fetches the results from the URL.
      applySearch(query, stateAbbrv);
      dispatch(setGlobalSearchReset(true));
      setShowSuggestions(false);
      setApiResponseReceived(false);
      setClickedEnter(false);
      setStateAbbrv("");
    }
  }, [apiResponseReceived, searchTableData, clickedEnter]);

  const debouncedFetch = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.trim().length >= 2) {
        const results = await fetchSuggestions(searchTerm);
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        setApiResponseReceived(false);
        setSearchTableData([]);
        setStateAbbrv("");
      }
    }, 500),
    [],
  );

  /** Fetch data when query changes */
  useEffect(() => {
    if (query?.trim().length >= 2) {
      debouncedFetch(query);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setClickedEnter(false);
      setApiResponseReceived(false);
      setSearchTableData([]);
      setStateAbbrv("");
      // NOTE: do NOT clear preSearchSnapshotRef here. Emptying the input to
      // type a new term is a normal mid-session action; clearing the snapshot
      // would make the next search re-capture an intermediate (search-result)
      // state, so clearing would restore stale search data instead of the
      // true pre-search state. The snapshot is cleared only on an explicit
      // clear (handleClearSearch) or an external reset (isGlobalSearchReset).
    }
  }, [query]);

  // Clear the search input when the search is reset externally (e.g. the user
  // switches view and confirms the "reset search?" popup), which flips
  // isGlobalSearchReset from true back to false.
  useEffect(() => {
    if (prevGlobalSearchResetRef.current === true && !isGlobalSearchReset) {
      setQuery("");
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      setClickedEnter(false);
      setApiResponseReceived(false);
      setSearchTableData([]);
      setStateAbbrv("");
      preSearchSnapshotRef.current = null;
    }
    prevGlobalSearchResetRef.current = isGlobalSearchReset;
  }, [isGlobalSearchReset]);

  // Clear the search input when an active search term is removed from the URL by
  // another action (e.g. applying advanced filters). A text search and advanced
  // filters can't be combined, so applying filters resets the search here too.
  useEffect(() => {
    if (prevUrlSearchRef.current && !urlSearch) {
      setQuery("");
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      setClickedEnter(false);
      setApiResponseReceived(false);
      setSearchTableData([]);
      setStateAbbrv("");
      preSearchSnapshotRef.current = null;
    }
    prevUrlSearchRef.current = urlSearch;
  }, [urlSearch]);

  /** Handle input change */
  const handleInputChange = (e) => {
    setQuery(e.target.value);
    // Editing the query invalidates any prior keyboard/selection highlight.
    // Without this, a leftover highlightedIndex makes Enter re-open a
    // suggestion instead of running the text search / table redirect.
    setHighlightedIndex(-1);
    // Also drop the previously selected hospital. It persists in Redux after a
    // suggestion pick and is never cleared, so onSearch() would otherwise
    // navigate a brand-new, un-highlighted search (e.g. "florida") straight to
    // the earlier hospital's factsheet instead of the search-results table.
    if (selectedHospital) dispatch(setSelectedHospital(null));
  };

  const checkIfAtEnd = () => {
    if (!suggestionsRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      suggestionsRef.current;

    const reachedEnd =
      scrollHeight - scrollTop - clientHeight <= 20;

    setIsAtEnd(reachedEnd);
  };

  useEffect(() => {
    setTimeout(() => {
      checkIfAtEnd();
    }, 0);
  }, [suggestions]);

  /** Handle keyboard navigation */
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => {
        const nextIndex = Math.min(prev + 1, suggestions.length - 1);
        scrollToHighlighted(nextIndex);
        return nextIndex;
      });
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) => {
        const nextIndex = Math.max(prev - 1, 0);
        scrollToHighlighted(nextIndex);
        return nextIndex;
      });
    } else if (e.key === "Enter") {
      if (highlightedIndex == -1) {
        if (query?.trim().length >= 2) {
          capturePreSearchSnapshot();
          setClickedEnter(true);
          dispatch(resetSelectedState());
          dispatch(resetSpecificStateData());
          // URL clearing + search term (+ state) are applied atomically once
          // results are available (see applySearch in handleSearchTableView /
          // the clickedEnter effect).
          if (apiResponseReceived) handleSearchTableView();
        }
      }
      if (highlightedIndex >= 0) {
        handleSuggestionClick(suggestions[highlightedIndex]);
      } else if (query?.trim().length >= 2) {
        onSearch();
      }
    }
  };

  const scrollToHighlighted = (index) => {
    const listElement = document.getElementById(`suggestion-${index}`);
    if (listElement) {
      listElement.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  };

  /** Handle suggestion selection */
  const handleSuggestionClick = (suggestion) => {
    // setQuery(suggestion.hosp10_name);
    setShowSuggestions(false);
    setQuery("");
    setHighlightedIndex(-1);

    dispatch(setSelectedHospital(suggestion));
    setClickedEnter(false);
    // FactSheet fetches (and caches) the hospital detail on navigation, so no
    // prefetch is needed here.
    navigate(`/hospitals/${suggestion.provider_number}`);
  };

  /** Close suggestions when clicking outside */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [suggestions]);

  return (
    <SearchBoxContainer ref={searchRef}>
      <Input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (query && suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
      />
      <BaseContainer
        onClick={() => {
          if (query?.length > 0) {
            handleClearSearch();
          }
        }}
        style={{
          borderRight: "none",
          cursor: query?.length > 0 ? "pointer" : "not-allowed",
        }}
      >
        {isLoading ? (
          <Loader />
        ) : query?.length > 0 ? (
          <CrossIcon
            style={{
              width: viewSizeCalculator(2),
              height: viewSizeCalculator(2),
            }}
          />
        ) : (
          <IconSearch />
        )}
      </BaseContainer>
      {showSuggestions && suggestions.length > 0 ? (
        <SuggestionsList isAtEnd={!isAtEnd}>
          <SuggestionsScroll 
              ref={suggestionsRef}
              onScroll={checkIfAtEnd}
            >
            {suggestions.map((suggestion, index) => (
              <ListElement
                key={index}
                id={`suggestion-${index}`} // Add an ID for reference
                className={index === highlightedIndex ? "highlighted" : ""}
                onMouseDown={() => handleSuggestionClick(suggestion)}
              >
                {suggestion.hosp10_name.toLowerCase()}
              </ListElement>
            ))}
          </SuggestionsScroll>
        </SuggestionsList>
      ) : (
        showSuggestions && (
          <SuggestionsList>
            <NotFoundWrapper style={{ minHeight: viewSizeCalculator(11) }}>
              <Text>No results found...</Text>
            </NotFoundWrapper>
          </SuggestionsList>
        )
      )}
    </SearchBoxContainer>
  );
};

export default SearchBox;
